import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { Principal, PrincipalRole, TenantDirectoryUser } from '../../app/types';
import PageSection from '../shared/PageSection';

interface TenantUsersPageProps {
  readonly principal: Principal;
  readonly tenantId: string;
  readonly users: TenantDirectoryUser[];
  readonly onCreateUser: (payload: {
    tenantId: string;
    email: string;
    role: PrincipalRole;
    username?: string;
    temporaryPassword?: string;
    sendInvite?: boolean;
  }) => Promise<void>;
  readonly onUpdateUser: (
    username: string,
    payload: { tenantId: string; role?: PrincipalRole; enabled?: boolean }
  ) => Promise<void>;
  readonly onResetPassword: (username: string) => Promise<void>;
  readonly onResendInvite: (username: string) => Promise<void>;
}

export default function TenantUsersPage({
  principal,
  tenantId,
  users,
  onCreateUser,
  onUpdateUser,
  onResetPassword,
  onResendInvite,
}: TenantUsersPageProps) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<PrincipalRole>('operator');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const roleOptions = useMemo(() => {
    if (principal.role === 'platform-admin') {
      return ['platform-admin', 'tenant-admin', 'operator', 'viewer'] as const;
    }
    return ['tenant-admin', 'operator', 'viewer'] as const;
  }, [principal.role]);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await onCreateUser({
        tenantId,
        email,
        username: username || undefined,
        role,
        temporaryPassword: temporaryPassword || undefined,
      });
      setEmail('');
      setUsername('');
      setRole(roleOptions[0]);
      setTemporaryPassword('');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to create user.');
    } finally {
      setSaving(false);
    }
  };

  const safeRoleUpdate = async (targetUsername: string, nextRole: PrincipalRole) => {
    try {
      setError('');
      await onUpdateUser(targetUsername, { tenantId, role: nextRole });
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : 'Unable to update role.');
    }
  };

  const safeStatusToggle = async (target: TenantDirectoryUser) => {
    try {
      setError('');
      await onUpdateUser(target.username, { tenantId, enabled: !target.enabled });
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : 'Unable to update status.');
    }
  };

  const triggerReset = async (targetUsername: string) => {
    try {
      setError('');
      await onResetPassword(targetUsername);
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : 'Unable to reset password.');
    }
  };

  const triggerResend = async (targetUsername: string) => {
    try {
      setError('');
      await onResendInvite(targetUsername);
    } catch (resendError) {
      setError(resendError instanceof Error ? resendError.message : 'Unable to resend invite.');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <PageSection title="Tenant Users" subtitle="Cognito users scoped to the active tenant.">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.username} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email ?? '-'}</TableCell>
                  <TableCell sx={{ minWidth: 170 }}>
                    <TextField
                      select
                      size="small"
                      value={user.role}
                      onChange={(event) => void safeRoleUpdate(user.username, event.target.value as PrincipalRole)}
                      fullWidth
                    >
                      {roleOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>{user.enabled ? 'Enabled' : 'Disabled'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button variant="outlined" size="small" onClick={() => void safeStatusToggle(user)}>
                        {user.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button variant="outlined" size="small" onClick={() => void triggerReset(user.username)}>
                        Reset Password
                      </Button>
                      <Button variant="outlined" size="small" onClick={() => void triggerResend(user.username)}>
                        Resend Invite
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">No users found for this tenant.</Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </PageSection>
      </Grid>

      <Grid item xs={12} lg={4}>
        <PageSection title="Create Tenant User" subtitle="Creates a Cognito user and sends an invite email.">
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <TextField
              label="Username (optional)"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
            <TextField
              select
              label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value as PrincipalRole)}
            >
              {roleOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Temporary Password (optional)"
              type="password"
              value={temporaryPassword}
              onChange={(event) => setTemporaryPassword(event.target.value)}
            />
            <Button variant="contained" onClick={() => void submit()} disabled={!email || saving}>
              {saving ? 'Creating...' : 'Create User'}
            </Button>
          </Stack>
        </PageSection>
      </Grid>
    </Grid>
  );
}
